/**
 * AI feature display names and engine type (Smart AI Summary vs paid LLM).
 * Smart AI Summary (Polars engine) features log with llm_model "python" / model_version "polars" in the backend.
 */

export const AI_FEATURE_ENGINE = {
  POLARS: 'polars',
  PAID: 'paid',
};

/** @type {Set<string>} */
export const POLARS_FEATURE_KEYS = new Set([
  'web_analytics_dashboard_summary_users_overview',
  'web_analytics_dashboard_summary_sessions_engagement_bounce_rate',
  'web_analytics_dashboard_summary_users_sessions_views_engagement',
  'web_analytics_dashboard_summary_users_traffic_by_channel',
  'web_analytics_dashboard_summary_by_user_device_category',
  'web_analytics_dashboard_summary_by_user_country',
  'web_analytics_dashboard_engagement_quality_overview',
  'web_analytics_dashboard_engagement_user_actions',
  'web_analytics_dashboard_eng_Rate_avg_eng_time_conversions_event_count_data',
  'web_analytics_dashboard_top_events',
  'web_analytics_dashboard_pages_screens',
  'conversion_overview',
  'dashboard_attribution_by_channel',
  'key_events',
  'smart_ai_summary',
]);

/** @type {Record<string, string>} */
export const AI_FEATURE_DISPLAY_NAMES = {
  // Smart AI Summary (free, Polars engine)
  smart_ai_summary: 'Smart AI Summary',
  web_analytics_dashboard_summary_users_overview: 'Web Analytics Dashboard — Users Overview Summary',
  web_analytics_dashboard_summary_sessions_engagement_bounce_rate:
    'Web Analytics Dashboard — Sessions, Engagement & Bounce Rate Summary',
  web_analytics_dashboard_summary_users_sessions_views_engagement:
    'Web Analytics Dashboard — Users, Sessions, Views & Engagement Summary',
  web_analytics_dashboard_summary_users_traffic_by_channel:
    'Web Analytics Dashboard — Traffic by Channel Summary',
  web_analytics_dashboard_summary_by_user_device_category:
    'Web Analytics Dashboard — Performance by User Device Category',
  web_analytics_dashboard_summary_by_user_country:
    'Web Analytics Dashboard — Performance by User Country',
  web_analytics_dashboard_engagement_quality_overview:
    'Web Analytics Dashboard — Engagement Quality Overview',
  web_analytics_dashboard_engagement_user_actions:
    'Web Analytics Dashboard — Engagement User Actions',
  web_analytics_dashboard_eng_Rate_avg_eng_time_conversions_event_count_data:
    'Web Analytics Dashboard — Engagement Rate, Avg Time, Conversions & Event Count',
  web_analytics_dashboard_top_events: 'Web Analytics Dashboard — Top Events Summary',
  web_analytics_dashboard_pages_screens: 'Web Analytics Dashboard — Pages & Screens Summary',
  conversion_overview: 'Conversion Overview Summary',
  dashboard_attribution_by_channel: 'Dashboard — Attribution by Channel Summary',
  key_events: 'Key Events Summary',

  // Paid LLM providers
  humanize_question: 'Humanize Question',
  analytics_summary: 'Analytics Summary',
  friction_intelligence: 'Friction Intelligence',
  session_summary: 'Session Summary',
  business_insights: 'Business Insights',
  seo_content_enhancement: 'SEO Content Enhancement',
  seo_content_regeneration: 'SEO Content Regeneration',
  friction_intelligence_issues_berief_report: 'Friction Intelligence — Issue Brief Report',
  friction_intelligence_business_impact_report: 'Friction Intelligence — Business Impact Report',
  report_analytics_summary: 'Report — Analytics Summary',
  report_weekly_analytics_summary: 'Report — Weekly Analytics Summary',
  report_channel_performance_analytics_summary: 'Report — Channel Performance Analytics Summary',
  report_product_and_monetization_analytics_summary:
    'Report — Product & Monetization Analytics Summary',
  report_content_monthly_analytics_summary: 'Report — Content Monthly Analytics Summary',
  report_content_weekly_analytics_summary: 'Report — Content Weekly Analytics Summary',
  content_channel_performance_report: 'Content — Channel Performance Report',
  content_page_performance_report: 'Content — Page Performance Report',
  content_lead_capture_and_contact_report: 'Content — Lead Capture & Contact Report',
  embeddings: 'Embeddings',
  chat_completion: 'Chat Completion',
};

export const getFeatureEngine = (featureKey) =>
  POLARS_FEATURE_KEYS.has(featureKey) ? AI_FEATURE_ENGINE.POLARS : AI_FEATURE_ENGINE.PAID;

export const getFeatureDisplayName = (featureKey) => {
  if (AI_FEATURE_DISPLAY_NAMES[featureKey]) {
    return AI_FEATURE_DISPLAY_NAMES[featureKey];
  }
  return featureKey.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

export const getFeatureShortLabel = (featureKey) => {
  const full = getFeatureDisplayName(featureKey);
  if (full.length <= 28) return full;
  const words = full.split(/\s+/);
  if (words.length <= 3) return `${full.slice(0, 25)}…`;
  return `${words.slice(0, 2).join(' ')}…`;
};

export const getFeatureEngineLabel = (engine) =>
  engine === AI_FEATURE_ENGINE.POLARS ? 'Smart AI Summary (free)' : 'Paid LLM';
